/** RaiderMapCR - Navigation bar component */
import React, { useEffect } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  useMediaQuery,
  BottomNavigation,
  BottomNavigationAction,
  Paper
} from '@mui/material';
import {
  Map as MapIcon,
  Home as HomeIcon,
  Schedule as ScheduleIcon,
  Restaurant as RestaurantIcon,
  ContactPhone as DirectoryIcon
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';

const Navbar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useMediaQuery('(max-width: 768px)');

  useEffect(() => {
    const viewport = document.querySelector('meta[name=viewport]');

    const setStableViewport = () => {
      if (viewport) {
        viewport.setAttribute(
          'content',
          'width=device-width, initial-scale=1, maximum-scale=1, user-scalable=no'
        );
      }
    };

    const handleFocusIn = () => setStableViewport();
    const handleFocusOut = () => setStableViewport();

    window.addEventListener('focusin', handleFocusIn);
    window.addEventListener('focusout', handleFocusOut);

    return () => {
      window.removeEventListener('focusin', handleFocusIn);
      window.removeEventListener('focusout', handleFocusOut);
    };
  }, []);

  useEffect(() => {
    const rootElement = document.getElementById('root');
    if (rootElement) {
      rootElement.scrollTop = 0;
    }
    window.scrollTo(0, 0);
  }, [location.pathname]);

  const handleNavigation = (path) => {
    navigate(path);
  };

  const menuItems = [
    { label: 'Home', path: '/', icon: <HomeIcon /> },
    { label: 'Map', path: '/map', icon: <MapIcon /> },
    { label: 'Courses', path: '/schedule', icon: <ScheduleIcon /> },
    { label: 'Directory', path: '/directory', icon: <DirectoryIcon /> },
    { label: 'Food', path: '/food-services', icon: <RestaurantIcon /> }
  ];

  const mobileNavItems = [
    { label: 'Map', path: '/map', icon: <MapIcon /> },
    { label: 'Courses', path: '/schedule', icon: <ScheduleIcon /> },
    { label: 'Home', path: '/', icon: <HomeIcon />, isCenter: true },
    { label: 'Directory', path: '/directory', icon: <DirectoryIcon /> },
    { label: 'Food', path: '/food-services', icon: <RestaurantIcon /> }
  ];

  const getCurrentValue = () => {
    const currentIndex = mobileNavItems.findIndex(item => item.path === location.pathname);
    return currentIndex >= 0 ? currentIndex : 2;
  };

  const handleBottomNavChange = (event, newValue) => {
    if (newValue >= 0 && newValue < mobileNavItems.length) {
      navigate(mobileNavItems[newValue].path);
    }
    if (event.target && 'ontouchstart' in window) {
      event.target.blur();
    }
  };

  return (
    <>
      <AppBar
        position="fixed"
        sx={{
          backgroundColor: 'rgba(42, 42, 42, 0.8)',
          borderBottom: '1px solid rgba(74, 74, 74, 0.4)',
          backdropFilter: 'blur(12px)',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)'
        }}
      >
        <Toolbar>
          {isMobile ? (
            <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', justifyContent: 'center' }}>
              <Typography
                variant="h5"
                component="div"
                sx={{ cursor: 'pointer', color: '#ffffff' }}
                onClick={() => navigate('/')}
              >
                RaiderMapCR <span style={{ fontSize: '0.6em', verticalAlign: 'super' }}>1.24</span>
              </Typography>
            </Box>
          ) : (
            <Typography
              variant="h4"
              component="div"
              sx={{ flexGrow: 1, cursor: 'pointer', color: '#ffffff' }}
              onClick={() => navigate('/')}
            >
              RaiderMapCR <span style={{ fontSize: '0.6em', verticalAlign: 'super' }}>1.24</span>
            </Typography>
          )}

          {!isMobile && (
            <Box sx={{ display: 'flex', gap: 0 }}>
              {menuItems.slice(1).map((item) => (
                <Button
                  key={item.path}
                  color="inherit"
                  onClick={() => handleNavigation(item.path)}
                  sx={{
                    minWidth: '75px',
                    width: '125px',
                    padding: '8px 10px',
                    borderRadius: 0,
                    backgroundColor: 'transparent',
                    margin: 0,

                    '&:hover': {
                      backgroundColor: 'rgba(255, 255, 255, 0.1)',
                      borderColor: 'rgba(255, 255, 255, 0.3)'
                    }
                  }}
                >
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
                    <Box sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: '40px',
                      height: '40px',
                      borderRadius: '50%',
                      backgroundColor: location.pathname === item.path ? 'rgba(209, 0, 0, 0.1)' : 'transparent',
                      border: location.pathname === item.path ? '1px solid rgba(209, 0, 0, 0.4)' : '1px solid transparent',
                      transition: 'all 0.2s ease',
                      '& .MuiSvgIcon-root': {
                        margin: 0,
                        padding: 0,
                        display: 'block',
                        fontSize: '26spx'
                      }
                    }}>
                      {React.cloneElement(item.icon, {
                        sx: {
                          fontSize: '24px',
                          color: location.pathname === item.path ? '#AA3333' : '#ffffff',
                          margin: 0,
                          padding: 0,
                          display: 'block'
                        }
                      })}
                    </Box>
                    <Typography
                      variant="body2"
                      sx={{
                        color: location.pathname === item.path ? '#AA3333' : '#ffffff',
                        fontSize: '0.85rem',
                        fontWeight: location.pathname === item.path ? 'bold' : '600',
                        letterSpacing: '0.5px'
                      }}
                    >
                      {item.label}
                    </Typography>
                  </Box>
                </Button>
              ))}
            </Box>
          )}
        </Toolbar>
      </AppBar>

      {isMobile && (
        <Paper
          sx={{
            position: 'fixed',
            bottom: 0,
            left: 0,
            right: 0,
            zIndex: 1100,
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
            backgroundColor: 'rgba(42, 42, 42, 0.8)',
            borderTop: '1px solid rgba(74, 74, 74, 0.4)',
            backdropFilter: 'blur(12px)',
            borderRadius: 0,
            margin: -1,
            padding: 0
          }}
          elevation={3}
        >
          <BottomNavigation
            value={getCurrentValue()}
            onChange={handleBottomNavChange}
            showLabels={false}
            sx={{
              height: 80,
              backgroundColor: 'transparent',
              '& .MuiBottomNavigationAction-root': {
                fontSize: '0.85rem',
                minWidth: 'auto',
                paddingTop: 0.5,
                paddingBottom: 0.5,
                color: '#ffffff',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'flex-start',
                alignItems: 'center',
                '@media (hover: hover)': {
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  }
                },
                '@media (hover: none)': {
                  '&:hover': {
                    backgroundColor: 'transparent',
                  }
                },
                '&.Mui-selected': {
                  color: '#AA3333',
                  '& .MuiSvgIcon-root': {
                    color: '#AA3333'
                  }
                },
                '& .MuiSvgIcon-root': {
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }
              }
            }}
          >
            {mobileNavItems.map((item, index) => (
              <BottomNavigationAction
                key={item.path}
                label=""
                icon={item.icon}
                sx={{
                  '& .MuiSvgIcon-root': {
                    fontSize: item.isCenter ? '2.2rem' : '1.8rem',
                    color: '#ffffff',
                    marginTop: item.isCenter ? '13px' : '16px'
                  },
                  color: '#ffffff',
                  '&.Mui-selected': {
                    color: '#AA3333',
                    '& .MuiSvgIcon-root': {
                      color: '#AA3333'
                    }
                  }
                }}
              />
            ))}
          </BottomNavigation>
        </Paper>
      )}
    </>
  );
};

export default Navbar;
